import React, { useState, useRef, useEffect } from "react";
import Cookies from "js-cookie";
import Highcharts from "highcharts";
import { useNavigate } from "react-router-dom";
import BarChartIcon from "@mui/icons-material/BarChart";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import InfoIcon from "@mui/icons-material/Info";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import SaveIcon from "@mui/icons-material/Save";
import DeleteIcon from "@mui/icons-material/Delete";
import GridViewIcon from "@mui/icons-material/GridView";
import TableChartIcon from "@mui/icons-material/TableChart";
import { fetchProfile } from "../../../utils/fetchProfile";
import Navbar from "../../../Components/Navbar";
import { LEVEL_OPTION } from "../../../../src/utils/constant";

const MODULE_NAME = "chart";
const BASE = process.env.REACT_APP_API_URL;
const COLORS = [
  "#07a3b5", "#f39c12", "#e74c3c", "#8e44ad", "#27ae60", "#2980b9", "#d35400",
  "#16a085", "#c0392b", "#34495e", "#1abc9c", "#e67e22", "#9b59b6", "#3498db",
  "#2ecc71", "#e91e63", "#ff5722", "#795548", "#607d8b", "#ff9800",
];
const CHART_TYPES = [
  { val: "column", label: "Bar Chart" },
  { val: "line", label: "Line Chart" },
];

const authHeader = (token) => ({
  Authorization: `Bearer ${token}`,
  "Content-Type": "application/json",
});

async function getJSON(token, url) {
  try {
    const res = await fetch(url, { headers: authHeader(token) });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json.data) ? json.data : [];
  } catch {
    return [];
  }
}

async function postJSON(token, url, body) {
  try {
    const res = await fetch(url, {
      method: "POST",
      headers: authHeader(token),
      body: JSON.stringify(body),
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.data || [];
  } catch {
    return [];
  }
}

async function fetchProductMaster(token, params = {}) {
  try {
    const url = new URL(`${BASE}/api/v1/product-master`);
    Object.keys(params).forEach((k) => {
      if (params[k] != null) url.searchParams.append(k, params[k]);
    });
    const res = await fetch(url.toString(), { headers: authHeader(token) });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json.data) ? json.data : [];
  } catch {
    return [];
  }
}

function getFactValue(row) {
  const v = row.FactValue ?? row.factvalue ?? row.Value ?? row.value ?? 0;
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
}

const emptyFilters = () => ({
  DB: "", Fact: [], Market: [], Period: [],
  Level: ["1_Category"], Category: [], Brand: [], Product: [],
});

function LoadingDots() {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6 }}>
      {[0, 1, 2].map((i) => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: "50%", background: "#07a3b5",
          animation: "dotPulse 1.2s ease-in-out infinite",
          animationDelay: `${i * 0.2}s`, display: "inline-block",
        }} />
      ))}
    </span>
  );
}

function SearchSelect({ label, options, value, onChange, required, placeholder = "Semua", loading = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) => String(o).toLowerCase().includes(q.toLowerCase()));
  const isErr = required && !value;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: isErr ? "#e74c3c" : "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6, display: "flex", alignItems: "center" }}>
        {label}{required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{ minHeight: 40, padding: "5px 10px", border: `1.5px solid ${isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"}`, borderRadius: 8, background: loading ? "#f8fafc" : "white", cursor: loading ? "not-allowed" : "pointer", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none", transition: "all .15s" }}
      >
        {value
          ? <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#07a3b5", color: "white", borderRadius: 20, padding: "2px 10px", fontSize: "0.78rem", fontWeight: 600, lineHeight: "22px" }}>
            {value}
            <button onClick={(e) => { e.stopPropagation(); onChange(""); }} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0, color: "white" }}><CloseIcon sx={{ fontSize: 13 }} /></button>
          </span>
          : <span style={{ fontSize: "0.85rem", color: loading ? "#b0bec5" : "#94a3b8", lineHeight: "28px" }}>
            {loading ? "Memuat opsi…" : placeholder}
          </span>}
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#94a3b8", marginLeft: "auto", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </div>
      {isErr && <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>Wajib dipilih</p>}
      {open && !loading && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "1.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,.12)", zIndex: 9999, overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 6 }}>
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari…" style={{ border: "none", outline: "none", fontSize: "0.82rem", width: "100%", fontFamily: "inherit", color: "#334155" }} />
          </div>
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            <button onClick={() => { onChange(""); setOpen(false); setQ(""); }} style={{ width: "100%", padding: "9px 12px", border: "none", background: !value ? "#f0fdfe" : "white", cursor: "pointer", fontSize: "0.82rem", textAlign: "left", fontFamily: "inherit", color: "#64748b", display: "flex", alignItems: "center", gap: 8 }}>
              {!value && <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />}<em>— {placeholder} —</em>
            </button>
            {filtered.map((opt) => (
              <button key={opt} onClick={() => { onChange(opt); setOpen(false); setQ(""); }} style={{ width: "100%", padding: "9px 12px", border: "none", background: value === opt ? "#f0fdfe" : "white", cursor: "pointer", fontSize: "0.82rem", textAlign: "left", fontFamily: "inherit", color: "#0f172a", display: "flex", alignItems: "center", gap: 8 }}>
                {value === opt && <CheckIcon sx={{ fontSize: 15, color: "#07a3b5" }} />}
                <span style={{ marginLeft: value === opt ? 0 : 23 }}>{opt}</span>
              </button>
            ))}
            {filtered.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>Tidak ditemukan</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function MultiSearchSelect({ label, options, value = [], onChange, required, placeholder = "Semua", hideSelectAll = false, loading = false }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);
  const filtered = options.filter((o) => String(o).toLowerCase().includes(q.toLowerCase()));
  const safeValue = Array.isArray(value) ? value : [];
  const isErr = required && safeValue.length === 0;

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (opt) => safeValue.includes(opt) ? onChange(safeValue.filter((v) => v !== opt)) : onChange([...safeValue, opt]);
  const selectAll = () => onChange(safeValue.length === options.length ? [] : [...options]);
  const remove = (opt, e) => { e.stopPropagation(); onChange(safeValue.filter((v) => v !== opt)); };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div style={{ fontSize: "0.72rem", fontWeight: 700, color: isErr ? "#e74c3c" : "#64748b", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6, display: "flex", alignItems: "center" }}>
        {label}{required && <span style={{ color: "#e74c3c" }}> *</span>}
        {loading && <LoadingDots />}
      </div>
      <div
        onClick={() => !loading && setOpen((p) => !p)}
        style={{ minHeight: 40, padding: "5px 10px", border: `1.5px solid ${isErr ? "#fca5a5" : open ? "#07a3b5" : "#e2e8f0"}`, borderRadius: 8, background: loading ? "#f8fafc" : "white", cursor: loading ? "not-allowed" : "pointer", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", boxShadow: open ? "0 0 0 3px rgba(7,163,181,0.12)" : "none", transition: "all .15s" }}
      >
        {safeValue.length === 0 && (
          <span style={{ fontSize: "0.85rem", color: loading ? "#b0bec5" : "#94a3b8", lineHeight: "28px" }}>
            {loading ? "Memuat opsi…" : placeholder}
          </span>
        )}
        {safeValue.map((v) => (
          <span key={v} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#e0f7fa", color: "#07a3b5", borderRadius: 20, padding: "2px 6px 2px 10px", fontSize: "0.78rem", fontWeight: 600, lineHeight: "22px" }}>
            {v}
            <button onClick={(e) => remove(v, e)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", padding: 0, color: "#07a3b5" }}><CloseIcon sx={{ fontSize: 13 }} /></button>
          </span>
        ))}
        <KeyboardArrowDownIcon sx={{ fontSize: 18, color: "#94a3b8", marginLeft: "auto", flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </div>
      {isErr && <p style={{ margin: "3px 0 0", fontSize: "0.7rem", color: "#e74c3c" }}>Wajib dipilih</p>}
      {open && !loading && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, background: "white", border: "1.5px solid #e2e8f0", borderRadius: 10, boxShadow: "0 8px 30px rgba(0,0,0,.12)", zIndex: 9999, overflow: "hidden" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 6 }}>
            <SearchIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari…" style={{ border: "none", outline: "none", fontSize: "0.82rem", width: "100%", fontFamily: "inherit", color: "#334155" }} onClick={(e) => e.stopPropagation()} />
            {q && <button onClick={() => setQ("")} style={{ border: "none", background: "none", cursor: "pointer", padding: 0, display: "flex" }}><CloseIcon sx={{ fontSize: 15, color: "#94a3b8" }} /></button>}
          </div>
          {!hideSelectAll && (
            <div style={{ padding: "4px 6px 2px", borderBottom: "1px solid #f1f5f9" }}>
              <button onClick={selectAll} style={{ width: "100%", padding: "7px 8px", border: "none", background: "none", cursor: "pointer", fontSize: "0.8rem", textAlign: "left", fontFamily: "inherit", color: "#475569", display: "flex", alignItems: "center", gap: 8, borderRadius: 6 }}>
                <div style={{ width: 16, height: 16, border: "1.5px solid", borderColor: safeValue.length === options.length ? "#07a3b5" : "#cbd5e1", borderRadius: 4, background: safeValue.length === options.length ? "#07a3b5" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {safeValue.length === options.length && <CheckIcon sx={{ fontSize: 11, color: "white" }} />}
                </div>
                <em>Pilih Semua ({options.length})</em>
              </button>
            </div>
          )}
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.map((opt) => (
              <button key={opt} onClick={() => toggle(opt)} style={{ width: "100%", padding: "8px 12px", border: "none", background: "none", cursor: "pointer", fontSize: "0.82rem", textAlign: "left", fontFamily: "inherit", color: "#0f172a", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 16, height: 16, border: "1.5px solid", borderColor: safeValue.includes(opt) ? "#07a3b5" : "#cbd5e1", borderRadius: 4, background: safeValue.includes(opt) ? "#07a3b5" : "white", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {safeValue.includes(opt) && <CheckIcon sx={{ fontSize: 11, color: "white" }} />}
                </div>
                {opt}
              </button>
            ))}
            {filtered.length === 0 && <div style={{ padding: 12, textAlign: "center", color: "#94a3b8", fontSize: "0.8rem" }}>Tidak ditemukan</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterZone({ title, icon: Icon, children, rightSlot }) {
  return (
    <div style={{ background: "#f8fafc", border: "1px solid #e8edf2", borderRadius: 10, padding: "16px 18px", marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {Icon && <Icon sx={{ fontSize: 18, color: "#64748b" }} />}
          <span style={{ fontWeight: 700, color: "#374151", fontSize: "0.92rem" }}>{title}</span>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{rightSlot}</div>
      </div>
      {children}
    </div>
  );
}

function TplModal({ open, mode, defaultName, onClose, onConfirm }) {
  const [name, setName] = useState("");
  useEffect(() => { if (open) setName(mode === "save" ? defaultName || "" : ""); }, [open, mode, defaultName]);
  if (!open) return null;
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}>
      <div style={{ background: "white", borderRadius: 14, padding: "28px 32px", width: 420, maxWidth: "92vw", boxShadow: "0 24px 60px rgba(0,0,0,.2)" }}>
        <h5 style={{ margin: "0 0 4px", fontWeight: 800, fontSize: "1.05rem", display: "flex", alignItems: "center", gap: 8 }}>
          <SaveIcon sx={{ fontSize: 20, color: "#07a3b5" }} />
          {mode === "save" ? "Simpan Template" : "Simpan Sebagai Baru"}
        </h5>
        <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: "0.82rem" }}>
          {mode === "save" ? "Perbarui template yang sedang aktif." : "Buat template baru dari konfigurasi saat ini."}
        </p>
        <label style={{ fontSize: "0.72rem", fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.6px" }}>Nama Template</label>
        <input
          type="text" value={name} autoFocus
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && name.trim() && onConfirm(name.trim())}
          placeholder="Cth: Analisis Jakarta Q1…"
          style={{ width: "100%", marginTop: 8, padding: "10px 12px", boxSizing: "border-box", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: "0.88rem", outline: "none", fontFamily: "inherit" }}
          onFocus={(e) => (e.target.style.borderColor = "#07a3b5")}
          onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
        />
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: "8px 18px", border: "1.5px solid #e2e8f0", borderRadius: 8, background: "white", color: "#475569", cursor: "pointer", fontWeight: 600, fontFamily: "inherit" }}>Batal</button>
          <button onClick={() => name.trim() && onConfirm(name.trim())} style={{ padding: "8px 20px", border: "none", borderRadius: 8, background: "#07a3b5", color: "white", cursor: "pointer", fontWeight: 700, fontFamily: "inherit" }}>
            {mode === "save" ? "Simpan" : "Buat Baru"}
          </button>
        </div>
      </div>
    </div>
  );
}

const iconBtn = (color) => ({
  border: `1.5px solid ${color}`, color, background: "transparent",
  borderRadius: 7, padding: 6, cursor: "pointer",
  display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit",
});

// X-axis = top-10 items (categories), series = one per period
function renderChart(container, series, categories, factLabel, chartType) {
  const fmt = (n) => new Intl.NumberFormat("id-ID").format(n);
  return Highcharts.chart(container, {
    chart: {
      type: chartType, backgroundColor: "white",
      style: { fontFamily: "inherit" },
      animation: { duration: 600 },
      reflow: true, height: 460,
      spacing: [20, 20, 40, 20],
    },
    title: { text: null },
    credits: { enabled: false },
    xAxis: {
      categories,
      crosshair: { color: "rgba(7,163,181,0.08)", width: chartType === "column" ? 40 : 1 },
      labels: {
        style: { fontSize: "11px", color: "#64748b", fontWeight: "500" },
        rotation: categories.some((c) => c.length > 15) ? -45 : 0,
        align: categories.some((c) => c.length > 15) ? "right" : "center",
        y: 20,
      },
      lineColor: "#e2e8f0", tickColor: "#e2e8f0",
      tickLength: 8,
    },
    yAxis: {
      title: { text: factLabel, style: { color: "#94a3b8", fontSize: "11px" } },
      gridLineColor: "#f1f5f9", gridLineDashStyle: "Dash",
      labels: {
        formatter() { return new Intl.NumberFormat("id-ID", { notation: "compact" }).format(this.value); },
        style: { fontSize: "11px", color: "#94a3b8" },
      },
    },
    legend: {
      enabled: true, align: "center", verticalAlign: "bottom", layout: "horizontal",
      itemStyle: { fontSize: "11px", fontWeight: "600", color: "#334155" },
      itemHoverStyle: { color: "#07a3b5" },
      maxHeight: 80,
      title: { text: "Periode", style: { fontSize: "10px", color: "#94a3b8", fontWeight: "500" } },
    },
    tooltip: {
      shared: true, backgroundColor: "white", borderColor: "#e2e8f0",
      borderRadius: 12,
      shadow: { color: "rgba(0,0,0,0.08)", offsetX: 0, offsetY: 4, opacity: 1, width: 16 },
      useHTML: true,
      formatter() {
        let s = `<div style="font-size:13px;font-weight:700;color:#0f172a;margin-bottom:8px;padding-bottom:6px;border-bottom:1px solid #f1f5f9">${this.x}</div>`;
        this.points.forEach((pt) => {
          s += `<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
            <span style="width:10px;height:10px;border-radius:50%;background:${pt.color};flex-shrink:0"></span>
            <span style="color:#64748b;font-size:12px;flex:1">${pt.series.name}</span>
            <b style="font-size:12px;color:#0f172a">${fmt(pt.y)}</b>
          </div>`;
        });
        return `<div style="padding:4px 2px;min-width:220px">${s}</div>`;
      },
    },
    plotOptions: {
      column: {
        borderRadius: 4, groupPadding: 0.1, pointPadding: 0.05,
        borderWidth: 0, minPointLength: 3, dataLabels: { enabled: false },
      },
      line: {
        marker: { radius: 5, lineColor: "#fff", lineWidth: 2, symbol: "circle" },
        lineWidth: 3,
      },
      series: {
        animation: { duration: 800 },
        states: { hover: { brightness: 0.1 } },
      },
    },
    series,
    responsive: {
      rules: [{ condition: { maxWidth: 600 }, chartOptions: { legend: { maxHeight: 50 } } }],
    },
  });
}

export default function ReportProjectedCustomChart() {
  const token = useRef(Cookies.get("access_token") || "").current;
  const navigate = useNavigate();

  const [profile, setProfile] = useState({});
  const [tpls, setTpls] = useState([]);
  const [selTpl, setSelTpl] = useState("");
  const [modal, setModal] = useState({ open: false, mode: "saveas", payload: {} });
  const [cf, setCf] = useState(emptyFilters);
  const [chartType, setChartType] = useState("column");
  const [chartReady, setChartReady] = useState(false);
  const [insight, setInsight] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadingDims, setLoadingDims] = useState({ Fact: false, Period: false, Market: false, Category: false, Products: false });
  const [dims, setDims] = useState({ DB: [], Fact: [], Period: [], Market: [], Category: [], Brand: [], Product: [] });
  const [masterCategories, setMasterCategories] = useState([]);
  const [masterProducts, setMasterProducts] = useState([]);

  const cfRef = useRef(cf);
  const chartTypeRef = useRef(chartType);
  useEffect(() => { cfRef.current = cf; }, [cf]);
  useEffect(() => { chartTypeRef.current = chartType; }, [chartType]);

  const chartRef = useRef(null);
  const hcRef = useRef(null);
  const loadedDB = useRef("");
  const loadedLevel = useRef("");
  const loadedCat = useRef("");

  const selectedLevels = cf.Level || [];
  const showCategory = selectedLevels.includes("1_Category") || selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
  const showBrand = selectedLevels.includes("3_Brand") || selectedLevels.includes("4_Product");
  const showProduct = selectedLevels.includes("4_Product");

  const brandOpts = masterProducts.length
    ? [...new Set(masterProducts.map((p) => p.brand_master))].filter(Boolean).sort()
    : dims.Brand;

  const prodOpts = (() => {
    let filtered = masterProducts;
    if (cf.Brand?.length) filtered = filtered.filter((p) => cf.Brand.includes(p.brand_master));
    return [...new Set(filtered.map((p) => p.product_name))].filter(Boolean).sort();
  })();

  useEffect(() => {
    fetchProfile(token, setProfile, []);
    getJSON(token, `${BASE}/api/v1/config_template?module_name=${MODULE_NAME}`).then(setTpls);
    getJSON(token, `${BASE}/api/v1/report_db/db_set`).then((dbs) =>
      setDims((p) => ({ ...p, DB: dbs }))
    );
  }, [token]);

  useEffect(() => {
    if (!cf.DB || cf.DB === loadedDB.current) return;
    loadedDB.current = cf.DB;
    loadedLevel.current = "";
    loadedCat.current = "";

    setDims((p) => ({ ...p, Fact: [], Period: [], Market: [], Category: [], Brand: [], Product: [] }));
    setMasterCategories([]);
    setMasterProducts([]);
    setLoadingDims((p) => ({ ...p, Fact: true, Period: true, Market: true }));

    const db = encodeURIComponent(cf.DB);
    Promise.all([
      getJSON(token, `${BASE}/api/v1/report_db/fact?db_set=${db}`),
      getJSON(token, `${BASE}/api/v1/report_db/period?db_set=${db}`),
      getJSON(token, `${BASE}/api/v1/report_db/market_breakdown?db_set=${db}`),
    ]).then(([facts, periods, markets]) => {
      setDims((p) => ({ ...p, Fact: facts, Period: periods, Market: markets }));
      setLoadingDims((p) => ({ ...p, Fact: false, Period: false, Market: false }));
    });
  }, [cf.DB, token]);

  useEffect(() => {
    if (!cf.DB) return;

    const levelKey = (cf.Level || []).slice().sort().join(",");
    const categoryKey = (cf.Category || []).slice().sort().join(",");
    const cacheKey = `${levelKey}|${categoryKey}`;
    if (cacheKey === loadedLevel.current) return;

    const needsCategory = showCategory;
    const needsProducts = showBrand || showProduct;

    if (!needsCategory && !needsProducts) {
      loadedLevel.current = cacheKey;
      return;
    }

    const loadMasterData = async () => {
      let cats = masterCategories;

      if (needsCategory && masterCategories.length === 0) {
        setLoadingDims((p) => ({ ...p, Category: true }));
        cats = await getJSON(token, `${BASE}/api/v1/category-master`);
        setMasterCategories(cats);
        setDims((p) => ({ ...p, Category: cats.map((c) => c.ord_category).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Category: false }));
      }

      if (needsProducts) {
        if (categoryKey === loadedCat.current) {
          loadedLevel.current = cacheKey;
          return;
        }
        loadedCat.current = categoryKey;
        setLoadingDims((p) => ({ ...p, Products: true }));

        let prods = [];
        if (cf.Category?.length > 0) {
          const ids = cf.Category.map((val) => {
            const obj = cats.find((c) => c.ord_category === val || c.name === val);
            return obj ? obj.id : null;
          }).filter(Boolean);
          prods = ids.length > 0
            ? await fetchProductMaster(token, { column_name: "category_master_id", column_operator: "IN", column_value: ids.join(",") })
            : await fetchProductMaster(token);
        } else {
          prods = await fetchProductMaster(token);
        }

        setMasterProducts(prods);
        setDims((p) => ({ ...p, Product: prods.map((pr) => pr.product_name).filter(Boolean) }));
        setLoadingDims((p) => ({ ...p, Products: false }));
      }

      loadedLevel.current = cacheKey;
    };

    loadMasterData();
  }, [cf.DB, cf.Level, cf.Category, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const isValid = !!(cf.DB && cf.Fact?.length && cf.Market?.length && cf.Period?.length && cf.Level?.length);

  function loadTpl(id) {
    if (!id) return;
    const t = tpls.find((x) => x.id === id);
    if (!t) return;
    try {
      const p = typeof t.filter_payload === "string" ? JSON.parse(t.filter_payload) : t.filter_payload;
      const loadedCf = { ...emptyFilters(), ...(p.cf || {}) };
      if (loadedCf.Fact && !Array.isArray(loadedCf.Fact)) loadedCf.Fact = [loadedCf.Fact];
      if (loadedCf.Market && !Array.isArray(loadedCf.Market)) loadedCf.Market = [loadedCf.Market];
      if (loadedCf.Period && !Array.isArray(loadedCf.Period)) loadedCf.Period = [loadedCf.Period];
      if (loadedCf.Level && !Array.isArray(loadedCf.Level)) loadedCf.Level = [loadedCf.Level];
      setCf(loadedCf);
      setChartType(p.chartType || "column");
    } catch (e) {
      console.error(e);
    }
  }

  async function reloadTpls() {
    const list = await getJSON(token, `${BASE}/api/v1/config_template?module_name=${MODULE_NAME}`);
    setTpls(list);
  }

  async function confirmModal(name) {
    const { mode, payload } = modal;
    if (mode === "save" && selTpl) {
      await fetch(`${BASE}/api/v1/config_template/${selTpl}`, {
        method: "PUT", headers: authHeader(token),
        body: JSON.stringify({ template_name: name, filter_payload: JSON.stringify(payload) }),
      });
    } else {
      const res = await fetch(`${BASE}/api/v1/config_template`, {
        method: "POST", headers: authHeader(token),
        body: JSON.stringify({ module_name: MODULE_NAME, template_name: name, filter_payload: JSON.stringify(payload) }),
      });
      if (res.ok) {
        const json = await res.json();
        if (json.data?.id) setSelTpl(json.data.id);
      }
    }
    await reloadTpls();
    setModal((m) => ({ ...m, open: false }));
  }

  async function deleteTpl() {
    if (!window.confirm("Hapus template ini?")) return;
    try {
      await fetch(`${BASE}/api/v1/config_template/${selTpl}`, { method: "DELETE", headers: authHeader(token) });
    } catch { }
    setSelTpl("");
    await reloadTpls();
  }

  // generateChart membaca dari cfRef.current dan chartTypeRef.current
  // sehingga SELALU mendapat nilai terbaru, tidak peduli kapan fungsi ini dibuat.
  async function generateChart() {
    const currentCf = cfRef.current;
    const currentChartType = chartTypeRef.current;

    const valid = !!(currentCf.DB && currentCf.Fact?.length && currentCf.Market?.length && currentCf.Period?.length && currentCf.Level?.length);
    if (!valid || busy) return;

    setBusy(true);
    setChartReady(false);
    setInsight(null);

    if (hcRef.current) { hcRef.current.destroy(); hcRef.current = null; }

    try {
      const body = {
        db_set: currentCf.DB,
        fact: currentCf.Fact.join(","),
        market_breakdown: currentCf.Market.join(","),
        level: currentCf.Level.join(","),
        period: currentCf.Period.join(","),
        size: 5000,
        page: 1,
      };
      if (currentCf.Category?.length) body.namagroup = currentCf.Category.join(",");
      if (currentCf.Brand?.length) body.brand = currentCf.Brand.join(",");
      if (currentCf.Product?.length) body.product = currentCf.Product.join(",");

      let data = await postJSON(token, `${BASE}/api/v1/report_db`, body);
      data = data.filter((d) => d.rpt_shortdesc && d.rpt_shortdesc !== ".Any");

      if (data.length === 0) { setBusy(false); return; }

      const periods = [...new Set(data.map((d) => String(d.Period)))].sort();

      const totals = {};
      data.forEach((d) => { totals[d.rpt_shortdesc] = (totals[d.rpt_shortdesc] || 0) + getFactValue(d); });
      const totalItemCount = Object.keys(totals).length;
      const top10 = Object.keys(totals).sort((a, b) => totals[b] - totals[a]).slice(0, 10);
      const wasLimited = totalItemCount > 10;

      // X-axis = top-10 items, series = periods (satu garis/bar per periode)
      const series = periods.map((period, idx) => ({
        name: period,
        color: COLORS[idx % COLORS.length],
        data: top10.map((item) => {
          const row = data.find((d) => String(d.Period) === period && d.rpt_shortdesc === item);
          return { y: row ? getFactValue(row) : 0 };
        }),
      }));

      let highVal = 0, highItem = "";
      top10.forEach((item) => {
        if (totals[item] > highVal) { highVal = totals[item]; highItem = item; }
      });

      const fmt = (n) => new Intl.NumberFormat("id-ID").format(n);
      const limitNote = wasLimited
        ? ` — data asli <b>${totalItemCount} item</b>, <span style="color:#f39c12;font-weight:700">hanya top 10 ditampilkan</span> (berdasarkan total nilai tertinggi)`
        : "";
      const insightText = `Menampilkan <b>${top10.length} item</b> × <b>${periods.length} periode</b>${limitNote}. Item terbesar: <b>${highItem}</b> dengan total <span style="color:#07a3b5;font-weight:700">${fmt(highVal)}</span>`;

      hcRef.current = renderChart(chartRef.current, series, top10, currentCf.Fact.join(", "), currentChartType);
      setChartReady(true);
      setInsight(insightText);
    } catch (err) {
      console.error("generateChart error:", err);
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setCf(emptyFilters());
    setChartReady(false);
    setInsight(null);
    loadedDB.current = "";
    loadedLevel.current = "";
    loadedCat.current = "";
    setMasterCategories([]);
    setMasterProducts([]);
    setDims((p) => ({ ...p, Fact: [], Period: [], Market: [], Category: [], Brand: [], Product: [] }));
    setLoadingDims({ Fact: false, Period: false, Market: false, Category: false, Products: false });
    if (hcRef.current) { hcRef.current.destroy(); hcRef.current = null; }
  }

  useEffect(() => {
    const onResize = () => { if (hcRef.current) hcRef.current.reflow(); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const row2Cols = 1 + (showCategory ? 1 : 0) + (showBrand ? 1 : 0) + (showProduct ? 1 : 0);

  const applyBtnStyle = (active) => ({
    display: "flex", alignItems: "center", gap: 6,
    border: "none", borderRadius: 8, padding: "7px 18px",
    cursor: active ? "pointer" : "not-allowed", fontWeight: 700,
    fontSize: "0.82rem", fontFamily: "inherit",
    background: active ? "#07a3b5" : "#e2e8f0",
    color: active ? "white" : "#94a3b8",
    boxShadow: active ? "0 3px 10px rgba(7,163,181,0.3)" : "none",
    transition: "all .2s",
  });

  return (
    <>
      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes dotPulse {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <Navbar active="Projected Reporting" initial={profile.initial} role_code={profile.role_code} />

      <TplModal
        open={modal.open} mode={modal.mode}
        defaultName={selTpl ? tpls.find((t) => t.id === selTpl)?.template_name : ""}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
        onConfirm={confirmModal}
      />

      <div style={{ background: "#f0f4f8", minHeight: "calc(100vh - 64px)" }}>
        <div style={{ maxWidth: "100%", margin: "0 auto", padding: 16 }}>

          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{ display: "flex", gap: 2, background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: 4, boxShadow: "0 1px 6px rgba(0,0,0,.06)" }}>
              <button onClick={() => navigate("/projected-reporting-pivot")} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 28px", borderRadius: 9, border: "none", cursor: "pointer", fontWeight: 500, fontSize: "0.88rem", fontFamily: "inherit", background: "transparent", color: "#64748b" }}>
                <GridViewIcon sx={{ fontSize: 18 }} /> Pivot Report View
              </button>
              <button onClick={() => navigate("/projected-reporting-tabular")} style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 28px", borderRadius: 9, border: "none", cursor: "pointer", fontWeight: 500, fontSize: "0.88rem", fontFamily: "inherit", background: "transparent", color: "#64748b" }}>
                <TableChartIcon sx={{ fontSize: 18 }} /> Tabular Report View
              </button>
              <button style={{ display: "flex", alignItems: "center", gap: 7, padding: "9px 28px", borderRadius: 9, border: "none", cursor: "pointer", fontWeight: 700, fontSize: "0.88rem", fontFamily: "inherit", background: "#07a3b5", color: "white", boxShadow: "0 3px 10px rgba(7,163,181,0.3)" }}>
                <BarChartIcon sx={{ fontSize: 18 }} /> Custom Chart Report View
              </button>
            </div>
          </div>

          <div style={{ background: "white", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,.06)", overflow: "hidden" }}>
            <div style={{ background: "#07a3b5", padding: "13px 22px", display: "flex", alignItems: "center", gap: 8 }}>
              <BarChartIcon sx={{ fontSize: 20, color: "white" }} />
              <span style={{ color: "white", fontWeight: 700, fontSize: "0.98rem" }}>Visual Analytics & Insights</span>
            </div>

            <div style={{ padding: 22 }}>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", padding: "12px 16px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, marginBottom: 20 }}>
                <BookmarkIcon sx={{ fontSize: 17, color: "#f59e0b" }} />
                <span style={{ fontWeight: 700, color: "#475569", fontSize: "0.82rem" }}>Template Chart:</span>
                <select
                  value={selTpl}
                  onChange={(e) => { setSelTpl(e.target.value); loadTpl(e.target.value); }}
                  style={{ fontSize: "0.82rem", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "6px 10px", color: "#334155", background: "white", minWidth: 180, outline: "none", cursor: "pointer", fontFamily: "inherit" }}
                >
                  <option value="">— Bawaan Pabrik —</option>
                  {tpls.map((t) => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                </select>
                <button onClick={() => setModal({ open: true, mode: selTpl ? "save" : "saveas", payload: { cf, chartType } })} style={iconBtn("#07a3b5")} title="Simpan"><SaveIcon sx={{ fontSize: 16 }} /></button>
                {selTpl && <button onClick={deleteTpl} style={iconBtn("#e74c3c")} title="Hapus"><DeleteIcon sx={{ fontSize: 16 }} /></button>}
              </div>

              <FilterZone
                title="Filter Analitik Chart"
                icon={FilterAltIcon}
                rightSlot={
                  <>
                    <select
                      value={chartType}
                      onChange={(e) => setChartType(e.target.value)}
                      style={{ fontSize: "0.82rem", border: "1.5px solid #e2e8f0", borderRadius: 8, padding: "7px 12px", color: "#334155", background: "white", outline: "none", cursor: "pointer", fontFamily: "inherit", fontWeight: 600, minWidth: 120 }}
                    >
                      {CHART_TYPES.map((ct) => <option key={ct.val} value={ct.val}>{ct.label}</option>)}
                    </select>
                    <button
                      onClick={reset}
                      style={{ display: "flex", alignItems: "center", gap: 5, border: "1.5px solid #e2e8f0", background: "white", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontWeight: 600, fontSize: "0.82rem", color: "#475569", fontFamily: "inherit" }}
                    >
                      <RestartAltIcon sx={{ fontSize: 16 }} /> Reset
                    </button>
                    <button onClick={generateChart} disabled={!isValid || busy} style={applyBtnStyle(isValid && !busy)}>
                      <AutoFixHighIcon sx={{ fontSize: 16 }} /> {busy ? "Loading…" : "Terapkan"}
                    </button>
                  </>
                }
              >
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 14 }}>
                  <SearchSelect
                    label="DB Set Name" options={dims.DB} value={cf.DB} required
                    onChange={(v) => {
                      setCf({ ...emptyFilters(), DB: v });
                      loadedDB.current = "";
                      loadedLevel.current = "";
                      loadedCat.current = "";
                      setMasterCategories([]);
                      setMasterProducts([]);
                    }}
                  />
                  <MultiSearchSelect
                    label="Fact" options={dims.Fact} value={cf.Fact} required
                    loading={loadingDims.Fact}
                    onChange={(v) => setCf((p) => ({ ...p, Fact: v }))}
                  />
                  <MultiSearchSelect
                    label="Period" options={dims.Period} value={cf.Period} required
                    loading={loadingDims.Period}
                    onChange={(v) => setCf((p) => ({ ...p, Period: v }))}
                  />
                  <MultiSearchSelect
                    label="Market" options={dims.Market} value={cf.Market} required
                    loading={loadingDims.Market}
                    onChange={(v) => setCf((p) => ({ ...p, Market: v }))}
                  />
                </div>

                <div style={{ display: "grid", gridTemplateColumns: `repeat(${row2Cols}, 1fr)`, gap: 14 }}>
                  <MultiSearchSelect
                    label="Level" options={LEVEL_OPTION} value={cf.Level}
                    onChange={(v) => {
                      setCf((p) => {
                        const next = { ...p, Level: v };
                        const hasBrand = v.includes("3_Brand") || v.includes("4_Product");
                        if (!hasBrand) { next.Brand = []; next.Product = []; }
                        else if (!v.includes("4_Product")) { next.Product = []; }
                        return next;
                      });
                    }}
                  />
                  {showCategory && (
                    <MultiSearchSelect
                      label="Category" options={dims.Category} value={cf.Category}
                      loading={loadingDims.Category}
                      onChange={(v) => setCf((p) => ({ ...p, Category: v, Brand: [], Product: [] }))}
                    />
                  )}
                  {showBrand && (
                    <MultiSearchSelect
                      label="Brand" options={brandOpts} value={cf.Brand}
                      loading={loadingDims.Products}
                      onChange={(v) => setCf((p) => ({ ...p, Brand: v, Product: [] }))}
                    />
                  )}
                  {showProduct && (
                    <MultiSearchSelect
                      label="Product" options={prodOpts} value={cf.Product}
                      loading={loadingDims.Products}
                      onChange={(v) => setCf((p) => ({ ...p, Product: v }))}
                    />
                  )}
                </div>
              </FilterZone>

              <div style={{ position: "relative", minHeight: 460, background: "#fafafa", border: "1.5px dashed #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                {!chartReady && !busy && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#94a3b8", gap: 8 }}>
                    <BarChartIcon sx={{ fontSize: 56, opacity: 0.1 }} />
                    <p style={{ fontWeight: 700, margin: 0, fontSize: "1rem" }}>Kanvas Kosong</p>
                    <p style={{ fontSize: "0.82rem", marginTop: 4, textAlign: "center" }}>
                      Pilih <span style={{ color: "#e74c3c", fontWeight: 700 }}>DB, Fact, Period, Market & Level</span> lalu klik <b>Terapkan</b>
                    </p>
                  </div>
                )}
                {busy && (
                  <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.85)", zIndex: 10 }}>
                    <div style={{ textAlign: "center", color: "#07a3b5" }}>
                      <div style={{ width: 40, height: 40, border: "3px solid #e2e8f0", borderTop: "3px solid #07a3b5", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
                      <p style={{ margin: 0, fontWeight: 600, fontSize: "0.88rem" }}>Memuat data…</p>
                    </div>
                  </div>
                )}
                <div ref={chartRef} style={{ width: "100%", height: 440 }} />
              </div>

              {insight && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, background: "#f0fdfe", border: "1px solid #a5f3fc", borderRadius: 9, padding: "11px 16px", marginTop: 14 }}>
                  <InfoIcon sx={{ fontSize: 17, color: "#07a3b5", flexShrink: 0 }} />
                  <p style={{ margin: 0, fontSize: "0.86rem", color: "#164e63", lineHeight: 1.55 }} dangerouslySetInnerHTML={{ __html: insight }} />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
